import CustomTooltip from "components/ui-component/CustomTooltip";
import { actionMenuItemClass } from "components/paymentMethod/PaymentMethodActionsMenu";
import Reconnect from "icons/dashboard/Reconnect";
import React, { useCallback, useEffect, useState } from "react";
import { PlaidLinkError, usePlaidLink } from "react-plaid-link";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import {
  getPlaidLinkToken,
  getPlaidPaymentToken,
} from "store/slices/paymentsSlice";
import { RootState } from "store/store";
import { decode } from "utils/HelperFunctions";
import { useTid } from "utils/testid";

const PlaidBankIconForm = ({
  editData,
  onOldCardDataGet,
  index,
  variant = "icon",
}: any) => {
  const tid = useTid();
  const { companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLinkTokenLoading, setIsLinkTokenLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<
    Parameters<typeof usePlaidLink>[0] | null
  >(null);
  const { clusterId: cluster_id, id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const reconnectId =
    editData?.id ||
    editData?.cppid ||
    editData?.pc_atid ||
    editData?.routing_number ||
    "bank";

  const getPaymentToken = async (data: any) => {
    setIsLinkTokenLoading(true);
    try {
      let clusterid = clusterId
        ? clusterId
        : userData?.data?.accountData?.clusterid
          ? userData?.data?.accountData?.clusterid
          : id
            ? decode(id || "")?.split("_")[1]
            : "";

      let payload = {
        public_token: data?.publicToken,
        bankAccountID: data?.accountId,
        clusterid: clusterid,
        accountid: userData?.data?.accountData?.accountid,
        cppid: editData?.id,
        isReconnect: true,
        slug: cluster_id || id || "",
      };
      let res: any = await dispatch(getPlaidPaymentToken(payload)).unwrap();
      if (res?.body?.success) {
        onOldCardDataGet();
      }
    } catch (error) {
      console.log("getPaymentTokenerror =====", error);
    } finally {
      setIsLinkTokenLoading(false);
      setLinkToken(null);
      setConfig(null);
    }
  };

  const onSuccess = useCallback(
    async (public_token?: string, metadata?: any) => {
      if (public_token && metadata) {
        let data = {
          publicToken: public_token,
          accountId: metadata?.account_id,
          bankName: metadata?.institution?.name,
        };
        await getPaymentToken(data);
      }
    },
    []
  );

  useEffect(() => {
    if (linkToken) {
      const newConfig: Parameters<typeof usePlaidLink>[0] = {
        token: linkToken,
        onSuccess: onSuccess,
        onExit: (error: PlaidLinkError | null) => {
          setLinkToken(null);
          setConfig(null);
        },
      };
      setConfig(newConfig);
    }
  }, [linkToken]);

  const { open, ready } = usePlaidLink(
    config || {
      token: "",
      onSuccess: () => {},
      onExit: () => {},
    }
  );

  useEffect(() => {
    if (ready && !isLinkTokenLoading && linkToken) {
      open();
    }
  }, [ready, linkToken, isLinkTokenLoading]);

  const getLinkToken = async () => {
    setIsLinkTokenLoading(true);
    try {
      let payload = {
        client_name:
          (companyDetails?.name || companyDetails?.company_name) ??
          "Palm Labs LLC",
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name?.trim(),
        email: userData?.data?.accountData?.email,
        clusterid: userData?.data?.accountData?.clusterid,
        slug: cluster_id || id || "",
      };
      let res: any = await dispatch(getPlaidLinkToken(payload)).unwrap();
      if (res?.body?.success) {
        setLinkToken(res?.body?.data?.link_token || null);
      }
    } catch (error) {
      console.log("getLinkToken error =====", error);
    } finally {
      setIsLinkTokenLoading(false);
    }
  };

  // Below `lg` this sits inside the tile's actions menu, so it renders as a
  // labelled row rather than the standalone hover icon.
  const isMenuItem = variant === "menuItem";

  return (
    <div
      className={
        isMenuItem
          ? `${actionMenuItemClass} text-txtPrimary cursor-pointer`
          : "cursor-pointer"
      }
      role={isMenuItem ? "menuitem" : "button"}
      tabIndex={0}
      data-testid={tid(`bank-reconnect-${reconnectId}-button`)}
      aria-label="Reconnect bank account"
      onClick={() => {
        if (!isLinkTokenLoading) {
          getLinkToken();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (!isLinkTokenLoading) {
            getLinkToken();
          }
        }
      }}
    >
      {isLinkTokenLoading ? (
        <div
          className={
            isMenuItem
              ? "flex items-center gap-[10px]"
              : "w-full h-full flex justify-center items-center"
          }
          data-testid={tid(`bank-reconnect-${reconnectId}-loading`)}
          aria-label="Loading bank reconnect"
        >
          <div
            className={`${
              isMenuItem ? "h-4 w-4" : "h-5 w-5"
            } animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]`}
          ></div>
          {isMenuItem && "Reconnect Bank"}
        </div>
      ) : isMenuItem ? (
        <>
          <span className="w-4 h-4 shrink-0 flex items-center justify-center">
            <Reconnect width={16} height={16} viewBox="7 7 20 20" />
          </span>
          Reconnect Bank
        </>
      ) : (
        <CustomTooltip
          maxwidth="430px"
          place="top"
          id={`bank-reconnect-${index}`}
          content={`<div class="text-xs leading-[18px] font-normal">Reconnect</div>`}
        >
          <Reconnect id={`bank-reconnect-${index}`} height="34" width="34" />
        </CustomTooltip>
      )}
    </div>
  );
};

export default PlaidBankIconForm;
